/**
 * PaymentRecordRow — the canonical "saved payment" row used everywhere
 * we display an existing payment in list form:
 *
 *   - Inline payments section on SegmentCard / GroupedFlightCard
 *   - Existing payments section inside EditSegmentDialog (rendered by
 *     DraftPaymentsSection via the render-prop)
 *
 * Look: dark card (sky-tinted border in dark mode) with prominent
 * amount + status pill, description + payment-method on the second
 * line, trash icon + right chevron on the right edge. Click anywhere
 * on the row to edit; click the trash to delete (stops propagation).
 *
 * Props:
 *   payment              — payment object
 *   paymentMethods       — array used to resolve method id → name
 *   onEdit(payment)      — click handler for row body (opens editor)
 *   onDelete(payment)    — click handler for trash icon
 *   formatCurrency       — shared currency formatter
 *   formatPoints         — shared points formatter
 *   testId               — base for data-testids on the row + trash btn
 *   compact              — slightly tighter padding (used inside dense
 *                          SegmentCard collapsed rows)
 */
import { Button } from "@/components/ui/button";
import { ChevronRight, Trash2 } from "lucide-react";

const PaymentRecordRow = ({
  payment,
  paymentMethods = [],
  onEdit,
  onDelete,
  formatCurrency,
  formatPoints,
  testId,
  compact = false,
}) => {
  const paymentMethod = paymentMethods.find((pm) => pm.id === payment.payment_method_id);
  const displayAmount =
    payment.currency === "POINTS"
      ? (formatPoints ? formatPoints(payment.amount) : `${payment.amount} pts`)
      : (formatCurrency ? formatCurrency(payment.amount, payment.currency) : `${payment.currency || "USD"} ${payment.amount}`);

  const rowId = testId || `payment-row-${payment.id}`;

  return (
    <div
      onClick={() => onEdit?.(payment)}
      className={`flex items-center justify-between ${compact ? "p-2" : "p-3"} bg-gray-50 dark:bg-slate-900/60 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 cursor-pointer transition-colors border border-gray-200 dark:border-slate-800`}
      data-testid={rowId}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{displayAmount}</span>
          <span
            className={`text-xs px-2 py-0.5 rounded ${
              payment.status === "paid"
                ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300"
            }`}
          >
            {payment.status}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
            {(payment.description || "").replace(/_/g, " ")}
          </span>
          {paymentMethod && (
            <>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {paymentMethod.payment_type === "points" && "⭐ "}
                {paymentMethod.name}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(payment);
            }}
            className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
            data-testid={`${rowId}-delete-btn`}
            aria-label="Delete payment"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        <ChevronRight className="h-4 w-4 text-gray-400" />
      </div>
    </div>
  );
};

export default PaymentRecordRow;
